import React, { useEffect, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { fuelCardsService } from '../../../services/fuelCards.service';
import type { CardActionRow } from '../../../services/fuelCards.service';
import { errMessage } from './errors';

/**
 * The expanded row on Fleet Management > Fuel Cards: one card's audit trail.
 *
 * This is where "why is this card blocked" gets answered, so two things are
 * deliberate:
 *
 * 1. `truncated` is rendered. `comdata_card_action` is append-only with no
 *    retention policy, so a busy card WILL eventually exceed the endpoint's
 *    limit, and a silently clipped trail would hide the very block someone came
 *    here to find.
 * 2. Every row shows its own outcome. `success` is `0 | 1` (never a boolean) and
 *    a failed attempt is as much of an answer as a successful one — a sweep that
 *    tried to block this card four times and timed out is the story.
 */

/** Corpay mixes formats elsewhere, but `created_at` is our own DATETIME. */
const formatStamp = (value: string | null): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

export interface CardHistoryProps {
  driverRecno: number;
}

export const CardHistory: React.FC<CardHistoryProps> = ({ driverRecno }) => {
  const [rows, setRows] = useState<CardActionRow[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [limit, setLimit] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    fuelCardsService
      .actions(driverRecno)
      .then((res) => {
        if (!live) return;
        setRows(res.data);
        setTruncated(res.truncated);
        setLimit(res.limit);
      })
      .catch((e: unknown) => {
        if (!live) return;
        // A 403 here is RBAC or a not-yet-deployed migration 433, never Comdata.
        setError(errMessage(
          e,
          'Could not load this card’s history.',
          'You do not have permission to read this card’s history (or migration 433 has not been deployed here).',
        ));
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [driverRecno]);

  return (
    <div className="space-y-2 bg-gray-50 px-6 py-4 dark:bg-gray-800/60">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
        Action history
      </h4>

      {loading && <p className="text-xs text-muted">Loading history…</p>}

      {error && (
        <div className="flex items-start gap-2 text-xs text-red-700 dark:text-red-300">
          <ExclamationTriangleIcon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <p className="text-xs italic text-muted">
          Nothing recorded for this card yet — no block, unblock, link or observed status change.
        </p>
      )}

      {rows.length > 0 && (
        <ul className="space-y-1">
          {rows.map((r) => (
            <li
              key={r.id}
              className="border-b border-gray-200 pb-1 text-xs last:border-b-0 dark:border-gray-700"
            >
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-muted">{formatStamp(r.created_at)}</span>
                <span className="font-medium text-body">{r.action}</span>
                <span className="text-muted">· {r.source}</span>
                <span className="text-muted">· {r.requested_by || '—'}</span>
                {/* `success` is 0 | 1, so === 1 rather than a truthiness test. */}
                <span className={r.success === 1
                  ? 'text-green-700 dark:text-green-400'
                  : 'text-red-700 dark:text-red-400'}
                >
                  · {r.success === 1 ? 'succeeded' : `failed${r.error ? `: ${r.error}` : ''}`}
                </span>
              </div>
              <div className="flex flex-wrap items-baseline gap-x-2 text-muted">
                {r.lock_reason && <span>reason {r.lock_reason}</span>}
                {r.status_before !== null || r.status_after !== null ? (
                  <span>{r.status_before ?? '—'} → {r.status_after ?? '—'}</span>
                ) : null}
                {r.response_message && <span>{r.response_message}</span>}
                {r.note && <span className="text-body">“{r.note}”</span>}
                {r.correlation_id && <span>id {r.correlation_id}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}

      {truncated && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          Showing the most recent {limit} entries — this card has more history than is shown here.
        </p>
      )}
    </div>
  );
};

export default CardHistory;
